import type { PlaceAutocompleteSelection, PlaceViewport } from '@db/Places';
import { MAP_INITIAL_REGION, getMapRegion, isValidMapCoordinate } from '@helpers/Map';
import type { MapCoordinate, MapRegion, MapViewport } from '@helpers/Map';

const AUTO_FIT_REGION_MARGIN_RATIO = 0.15;
const AUTO_FIT_MIN_DELTA = 0.5;
const AUTO_FIT_LATITUDE_MIN = -90;
const AUTO_FIT_LATITUDE_MAX = 90;
const MIN_MARKER_REGION_MOVE_RATIO = 0.4;
const MIN_MARKER_REGION_ZOOM_RATIO = 0.4;
const MIN_PLACE_VIEWPORT_DELTA = 0.0001;
const PLACE_FALLBACK_VIEWPORT_DELTA = 0.5;
const MAX_PLACE_VIEWPORT_LONGITUDE_DELTA = 180;

type NonEmptyMapCoordinates = [MapCoordinate, ...MapCoordinate[]];
type CoordinateSpan = {
  max: number;
  min: number;
};
type CoordinateBounds = {
  latitude: CoordinateSpan;
  // Longitude spans may be unwrapped past 180 so the shortest antimeridian-crossing span wins.
  longitude: CoordinateSpan;
};

export function getPlaceSearchSelectionMapRegion(selection: PlaceAutocompleteSelection): MapRegion {
  return (
    getPlaceViewportMapRegion(selection.viewport) ??
    getMapRegion({
      ...selection.place,
      latitudeDelta: PLACE_FALLBACK_VIEWPORT_DELTA,
      longitudeDelta: PLACE_FALLBACK_VIEWPORT_DELTA,
    })
  );
}

export function getPlaceViewportMapRegion(viewport?: PlaceViewport): MapRegion | undefined {
  if (!isValidPlaceViewport(viewport)) return undefined;

  const latitudeDelta = Math.max(
    MIN_PLACE_VIEWPORT_DELTA,
    viewport.high.latitude - viewport.low.latitude
  );
  const longitudeDelta = Math.min(
    MAX_PLACE_VIEWPORT_LONGITUDE_DELTA,
    Math.max(
      MIN_PLACE_VIEWPORT_DELTA,
      getWrappedLongitudeDelta(viewport.low.longitude, viewport.high.longitude)
    )
  );

  return {
    latitude: (viewport.low.latitude + viewport.high.latitude) / 2,
    longitude: getWrappedLongitudeCenter(viewport.low.longitude, viewport.high.longitude),
    latitudeDelta,
    longitudeDelta,
  };
}

export function coordinateInPlaceViewport(coordinate: MapCoordinate, viewport?: PlaceViewport) {
  if (!isValidMapCoordinate(coordinate) || !isValidPlaceViewport(viewport)) return undefined;

  return (
    coordinate.latitude >= viewport.low.latitude &&
    coordinate.latitude <= viewport.high.latitude &&
    longitudeInWrappedRange(coordinate.longitude, viewport.low.longitude, viewport.high.longitude)
  );
}

export function coordinateInMapRegion(coordinate: MapCoordinate, region: MapRegion) {
  if (!isValidMapCoordinate(coordinate)) return false;

  return (
    Math.abs(coordinate.latitude - region.latitude) <=
      getNormalizedRegionDelta(region.latitudeDelta) / 2 &&
    Math.abs(getShortestLongitudeDelta(region.longitude, coordinate.longitude)) <=
      getNormalizedRegionDelta(region.longitudeDelta) / 2
  );
}

export function getCoordinateRegionDistanceRatio(coordinate: MapCoordinate, region: MapRegion) {
  const latitudeDistance =
    Math.abs(coordinate.latitude - region.latitude) /
    Math.max(getNormalizedRegionDelta(region.latitudeDelta) / 2, MIN_PLACE_VIEWPORT_DELTA);
  const longitudeDistance =
    Math.abs(getShortestLongitudeDelta(region.longitude, coordinate.longitude)) /
    Math.max(getNormalizedRegionDelta(region.longitudeDelta) / 2, MIN_PLACE_VIEWPORT_DELTA);

  return Math.sqrt(latitudeDistance ** 2 + longitudeDistance ** 2);
}

export function getCoordinateViewportPoint(
  coordinate: MapCoordinate,
  mapRegion: MapRegion,
  mapViewport: MapViewport
) {
  const viewport = getNormalizedMapViewport(mapViewport);
  const latitudeDelta = getNormalizedRegionDelta(mapRegion.latitudeDelta);
  const longitudeDelta = getNormalizedRegionDelta(mapRegion.longitudeDelta);

  return {
    x:
      (getShortestLongitudeDelta(mapRegion.longitude, coordinate.longitude) / longitudeDelta +
        0.5) *
      viewport.width,
    y: ((mapRegion.latitude - coordinate.latitude) / latitudeDelta + 0.5) * viewport.height,
  };
}

export function getAverageMapCoordinate(coordinates: NonEmptyMapCoordinates): MapCoordinate {
  const coordinateSum = coordinates.reduce(
    (sum, coordinate) => {
      const longitudeRadians = deg2rad(coordinate.longitude);
      return {
        latitude: sum.latitude + coordinate.latitude,
        longitudeX: sum.longitudeX + Math.cos(longitudeRadians),
        longitudeY: sum.longitudeY + Math.sin(longitudeRadians),
      };
    },
    { latitude: 0, longitudeX: 0, longitudeY: 0 }
  );
  const longitude = normalizeLongitude(
    rad2deg(Math.atan2(coordinateSum.longitudeY, coordinateSum.longitudeX))
  );

  return {
    latitude: coordinateSum.latitude / coordinates.length,
    longitude,
  };
}

export function getCoordinateFitRegion(
  coordinates: (MapCoordinate | undefined)[]
): MapRegion | undefined {
  const validCoordinates = coordinates.filter(isValidMapCoordinate);
  if (validCoordinates.length === 0) return undefined;

  const bounds = getCoordinateBounds(validCoordinates);
  const latitudeDelta = getFitRegionDelta(bounds.latitude, MAP_INITIAL_REGION.latitudeDelta);
  const longitudeDelta = getFitRegionDelta(bounds.longitude, MAP_INITIAL_REGION.longitudeDelta);

  if (
    getPaddedCoordinateSpan(bounds.latitude) <= MAP_INITIAL_REGION.latitudeDelta &&
    getPaddedCoordinateSpan(bounds.longitude) <= MAP_INITIAL_REGION.longitudeDelta
  ) {
    return getRegionForBounds(bounds, { latitudeDelta, longitudeDelta });
  }

  // If all coordinates cannot fit, choose the region that shows the most coordinates,
  // then tighten the final region around that visible cluster.
  return getMaxCoverageFitRegion(validCoordinates, { latitudeDelta, longitudeDelta });
}

export function regionsEqual(regionA: MapRegion, regionB: MapRegion) {
  return (
    regionA.latitude === regionB.latitude &&
    regionA.longitude === regionB.longitude &&
    regionA.latitudeDelta === regionB.latitudeDelta &&
    regionA.longitudeDelta === regionB.longitudeDelta
  );
}

export function hasMarkerRegionChangedEnough(previous: MapRegion, next: MapRegion) {
  const latitudeMoveRatio =
    Math.abs(previous.latitude - next.latitude) /
    Math.max(previous.latitudeDelta, next.latitudeDelta, Number.EPSILON);
  const longitudeMoveRatio =
    Math.abs(getShortestLongitudeDelta(previous.longitude, next.longitude)) /
    Math.max(previous.longitudeDelta, next.longitudeDelta, Number.EPSILON);
  const latitudeZoomRatio = getRelativeDeltaChange(previous.latitudeDelta, next.latitudeDelta);
  const longitudeZoomRatio = getRelativeDeltaChange(previous.longitudeDelta, next.longitudeDelta);

  return (
    Math.max(latitudeMoveRatio, longitudeMoveRatio) >= MIN_MARKER_REGION_MOVE_RATIO ||
    Math.max(latitudeZoomRatio, longitudeZoomRatio) >= MIN_MARKER_REGION_ZOOM_RATIO
  );
}

export function getShortestLongitudeDelta(fromLongitude: number, toLongitude: number) {
  const normalizedDelta = normalizeLongitude(toLongitude - fromLongitude);
  return normalizedDelta === -180 ? 180 : normalizedDelta;
}

export function normalizeLongitude(longitude: number) {
  const normalized = ((((longitude + 180) % 360) + 360) % 360) - 180;
  return normalized === -180 && longitude > 0 ? 180 : normalized;
}

function isValidPlaceViewport(viewport?: PlaceViewport): viewport is PlaceViewport {
  if (!isValidMapCoordinate(viewport?.low)) return false;
  if (!isValidMapCoordinate(viewport?.high)) return false;
  if (viewport.low.latitude > viewport.high.latitude) return false;
  if (viewport.low.longitude === 180 && viewport.high.longitude === -180) return false;
  return true;
}

function longitudeInWrappedRange(longitude: number, lowLongitude: number, highLongitude: number) {
  const normalizedLongitude = normalizeLongitude(longitude);
  const normalizedLowLongitude = normalizeLongitude(lowLongitude);
  const normalizedHighLongitude = normalizeLongitude(highLongitude);

  if (normalizedLowLongitude <= normalizedHighLongitude) {
    return (
      normalizedLongitude >= normalizedLowLongitude &&
      normalizedLongitude <= normalizedHighLongitude
    );
  }

  return (
    normalizedLongitude >= normalizedLowLongitude || normalizedLongitude <= normalizedHighLongitude
  );
}

function getWrappedLongitudeCenter(lowLongitude: number, highLongitude: number) {
  if (lowLongitude <= highLongitude) return (lowLongitude + highLongitude) / 2;

  const wrappedCenter = (lowLongitude + highLongitude + 360) / 2;
  return normalizeLongitude(wrappedCenter);
}

function getWrappedLongitudeDelta(lowLongitude: number, highLongitude: number) {
  if (lowLongitude <= highLongitude) return highLongitude - lowLongitude;
  return 360 - lowLongitude + highLongitude;
}

function getNormalizedMapViewport(mapViewport: MapViewport) {
  return {
    width: Math.max(mapViewport.width, 1),
    height: Math.max(mapViewport.height, 1),
  };
}

function getNormalizedRegionDelta(delta: number) {
  return Math.max(Math.abs(delta), Number.EPSILON);
}

function getMaxCoverageFitRegion(
  coordinates: MapCoordinate[],
  deltas: Pick<MapRegion, 'latitudeDelta' | 'longitudeDelta'>
) {
  const latitudeCandidates = getCoverageCandidateCenters(
    coordinates.map((coordinate) => coordinate.latitude),
    deltas.latitudeDelta,
    AUTO_FIT_LATITUDE_MIN,
    AUTO_FIT_LATITUDE_MAX
  );
  const longitudeCandidates = getCoverageCandidateCenters(
    coordinates.map((coordinate) => coordinate.longitude),
    deltas.longitudeDelta
  );
  let bestRegion: MapRegion | undefined;
  let bestVisibleCoordinates: MapCoordinate[] = [];
  let bestAverageDistance = Number.POSITIVE_INFINITY;

  for (const latitude of latitudeCandidates) {
    for (const longitude of longitudeCandidates) {
      const region = { latitude, longitude, ...deltas };
      const visibleCoordinates = coordinates.filter((coordinate) =>
        coordinateInFitRegion(coordinate, region)
      );
      const averageDistance = getAverageCoordinateDistance(visibleCoordinates, region);

      if (
        !bestRegion ||
        visibleCoordinates.length > bestVisibleCoordinates.length ||
        (visibleCoordinates.length === bestVisibleCoordinates.length &&
          averageDistance < bestAverageDistance)
      ) {
        bestRegion = region;
        bestVisibleCoordinates = visibleCoordinates;
        bestAverageDistance = averageDistance;
      }
    }
  }

  if (bestVisibleCoordinates.length === 0) return bestRegion;

  return getRegionForBounds(getCoordinateBounds(bestVisibleCoordinates), deltas);
}

function getCoverageCandidateCenters(
  values: number[],
  delta: number,
  minValue?: number,
  maxValue?: number
) {
  const centers = new Set<number>();

  values.forEach((value) => {
    addCoverageCandidateCenter(centers, value, delta, minValue, maxValue);
    addCoverageCandidateCenter(centers, value - delta / 2, delta, minValue, maxValue);
    addCoverageCandidateCenter(centers, value + delta / 2, delta, minValue, maxValue);
  });

  return [...centers.values()];
}

function addCoverageCandidateCenter(
  centers: Set<number>,
  center: number,
  delta: number,
  minValue?: number,
  maxValue?: number
) {
  centers.add(
    minValue === undefined || maxValue === undefined
      ? normalizeLongitude(center)
      : clampCoverageCenter(center, delta, minValue, maxValue)
  );
}

function clampCoverageCenter(center: number, delta: number, minValue: number, maxValue: number) {
  const halfDelta = delta / 2;
  return Math.min(Math.max(center, minValue + halfDelta), maxValue - halfDelta);
}

function getRegionForBounds(
  bounds: CoordinateBounds,
  deltas: Pick<MapRegion, 'latitudeDelta' | 'longitudeDelta'>
): MapRegion {
  return {
    latitude: getCoordinateSpanCenter(bounds.latitude),
    longitude: normalizeLongitude(getCoordinateSpanCenter(bounds.longitude)),
    latitudeDelta: getFitRegionDelta(bounds.latitude, deltas.latitudeDelta),
    longitudeDelta: getFitRegionDelta(bounds.longitude, deltas.longitudeDelta),
  };
}

function getCoordinateBounds(coordinates: MapCoordinate[]): CoordinateBounds {
  return {
    latitude: getCoordinateSpan(coordinates.map((coordinate) => coordinate.latitude)),
    longitude: getWrappedLongitudeSpan(coordinates.map((coordinate) => coordinate.longitude)),
  };
}

function coordinateInFitRegion(coordinate: MapCoordinate, region: MapRegion) {
  return (
    Math.abs(coordinate.latitude - region.latitude) <= region.latitudeDelta / 2 &&
    Math.abs(getShortestLongitudeDelta(region.longitude, coordinate.longitude)) <=
      region.longitudeDelta / 2
  );
}

function getAverageCoordinateDistance(coordinates: MapCoordinate[], region: MapRegion) {
  if (coordinates.length === 0) return Number.POSITIVE_INFINITY;

  const totalDistance = coordinates.reduce(
    (sum, coordinate) =>
      sum +
      Math.hypot(
        (coordinate.latitude - region.latitude) / region.latitudeDelta,
        getShortestLongitudeDelta(region.longitude, coordinate.longitude) / region.longitudeDelta
      ),
    0
  );

  return totalDistance / coordinates.length;
}

function getFitRegionDelta(span: CoordinateSpan, maxDelta: number) {
  return Math.min(getPaddedCoordinateSpan(span), maxDelta);
}

function getPaddedCoordinateSpan(span: CoordinateSpan) {
  const coordinateSpan = getCoordinateSpanSize(span);
  return Math.max(coordinateSpan * (1 + AUTO_FIT_REGION_MARGIN_RATIO * 2), AUTO_FIT_MIN_DELTA);
}

function getCoordinateSpan(values: number[]): CoordinateSpan {
  return {
    max: Math.max(...values),
    min: Math.min(...values),
  };
}

function getWrappedLongitudeSpan(longitudes: number[]): CoordinateSpan {
  if (longitudes.length === 1) {
    const longitude = toPositiveLongitudeDegrees(longitudes[0]);
    return { min: longitude, max: longitude };
  }

  const sortedLongitudes = longitudes
    .map(toPositiveLongitudeDegrees)
    .sort((longitudeA, longitudeB) => longitudeA - longitudeB);
  let largestGapIndex = 0;
  let largestGap = Number.NEGATIVE_INFINITY;

  sortedLongitudes.forEach((longitude, index) => {
    const nextLongitude =
      index === sortedLongitudes.length - 1
        ? sortedLongitudes[0] + 360
        : sortedLongitudes[index + 1];
    const gap = nextLongitude - longitude;

    if (gap > largestGap) {
      largestGap = gap;
      largestGapIndex = index;
    }
  });

  const minLongitude = sortedLongitudes[(largestGapIndex + 1) % sortedLongitudes.length];
  const rawMaxLongitude = sortedLongitudes[largestGapIndex];
  const maxLongitude = rawMaxLongitude < minLongitude ? rawMaxLongitude + 360 : rawMaxLongitude;

  return { min: minLongitude, max: maxLongitude };
}

function getCoordinateSpanCenter(span: CoordinateSpan) {
  return (span.min + span.max) / 2;
}

function getCoordinateSpanSize(span: CoordinateSpan) {
  return span.max - span.min;
}

function toPositiveLongitudeDegrees(longitude: number) {
  const normalizedLongitude = normalizeLongitude(longitude);
  return normalizedLongitude < 0 ? normalizedLongitude + 360 : normalizedLongitude;
}

function getRelativeDeltaChange(previous: number, next: number) {
  return Math.abs(previous - next) / Math.max(previous, next, Number.EPSILON);
}

function deg2rad(deg: number) {
  return deg * (Math.PI / 180);
}

function rad2deg(rad: number) {
  return rad * (180 / Math.PI);
}
